import type { ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';

import type { CSSProperties } from 'react';
import type { WhiteLabellingConfig } from 'components-stateless/constants';
import { COLOURS_AA } from 'components-stateless/DesignSystem/Atoms/Colours';

declare module '@mui/material/styles' {
  interface Theme {
    appDrawer: {
      width: React.CSSProperties['width'];
      breakpoint: string;
    };
    faviconURL: string;
    logoURL: string;
    navBarBackgroundColor: React.CSSProperties['color'];
    navBarFontColor: React.CSSProperties['color'];
    uiControlBackgroundColor: React.CSSProperties['color'];
    uiControlFontColor: React.CSSProperties['color'];
    allowWhitelabelling?: boolean;
  }

  interface Components {
    MuiPickersToolbar?: {
      styleOverrides?: {
        toolbar?: React.CSSProperties;
      };
    };
    MuiPickersDay?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
    MuiPickersModal?: {
      styleOverrides?: {
        dialogAction?: React.CSSProperties;
      };
    };
    MuiPickersClock?: {
      styleOverrides?: {
        clock?: React.CSSProperties;
      };
    };
    MuiPickersClockPointer?: {
      styleOverrides?: {
        pointer?: React.CSSProperties;
      };
    };
    MuiNavbarToolbar?: {
      styleOverrides?: {
        root?: React.CSSProperties;
      };
    };
  }
}

const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: COLOURS_AA.primary.BtnPrimaryHover,
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'Open Sans',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 400,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          backgroundColor: '#fff', // Set table toolbars to white
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        toolbar: {
          backgroundColor: COLOURS_AA.primary.BtnPrimaryHover,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &:hover, &.Mui-selected:focus': {
            color: '#fff',
            backgroundColor: COLOURS_AA.primary.BtnPrimaryHover,
          },
        } as CSSProperties,
      },
    },
    MuiPickersModal: {
      styleOverrides: {
        dialogAction: {
          color: COLOURS_AA.primary.BtnPrimaryHover,
        },
      },
    },
    // Optionally override clock components if needed:
    MuiPickersClock: {
      styleOverrides: {
        clock: {
          backgroundColor: COLOURS_AA.primary.BtnPrimaryHover,
        },
      },
    },
    MuiPickersClockPointer: {
      styleOverrides: {
        pointer: {
          backgroundColor: COLOURS_AA.primary.BtnPrimaryHover,
        },
      },
    },
  },
};

// export const theme = createTheme(baseThemeOptions);

export const createThemeWithWhiteLabelling = (
  whiteLabelling: WhiteLabellingConfig
) =>
  createTheme({
    ...baseThemeOptions,
    palette: {
      ...baseThemeOptions.palette,
      primary: {
        main: COLOURS_AA.primary.BtnPrimaryHover,
        contrastText: whiteLabelling.uiControlFontColor,
      },
      secondary: {
        main: whiteLabelling.uiControlBackgroundColor,
        contrastText: whiteLabelling.uiControlFontColor,
      },
    },
    components: {
      ...baseThemeOptions.components,
      MuiNavbarToolbar: {
        styleOverrides: {
          root: {
            width: '100%',
            backgroundColor: whiteLabelling.navBarBackgroundColor,
            zIndex: 10,
            minHeight: '3.6rem',
            height: '3.6rem',
          },
        },
      },
    },
  });
